import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import ContactGroupService from '../../services/contact-group.service';

const ContactGroupList: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#1976d2' });
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await ContactGroupService.getAll({
        populate: '*',
        'pagination[pageSize]': 100,
      });
      setGroups(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingGroup) {
        await ContactGroupService.update(editingGroup.id, formData);
      } else {
        await ContactGroupService.create(formData);
      }
      setOpenDialog(false);
      setEditingGroup(null);
      setFormData({ name: '', description: '', color: '#1976d2' });
      fetchGroups();
    } catch (error) {
      console.error('Error saving group:', error);
    }
  };

  const handleEdit = (group: any) => {
    setEditingGroup(group);
    setFormData({
      name: group.attributes?.name,
      description: group.attributes?.description || '',
      color: group.attributes?.color || '#1976d2',
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        await ContactGroupService.delete(id);
        fetchGroups();
      } catch (error) {
        console.error('Error deleting group:', error);
      }
    }
  };

  const handleImportClick = (group: any) => {
    setSelectedGroup(group);
    setImportFile(null);
    setImportError('');
    setImportSuccess('');
    setOpenImportDialog(true);
  };

  const downloadTemplate = () => {
    const csvContent = 'firstName,lastName,phone,email,whatsappNumber,company,position,address,city,country,notes\nJohn,Doe,+1234567890,john@example.com,+1234567890,Acme Corp,Manager,123 Main St,New York,USA,Sample contact';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'contacts_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
      ];
      if (validTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setImportFile(file);
        setImportError('');
      } else {
        setImportError('Please upload a valid Excel (.xlsx, .xls) or CSV file');
        setImportFile(null);
      }
    }
  };

  const handleImport = async () => {
    if (!importFile || !selectedGroup) return;

    try {
      setImporting(true);
      setImportError('');
      setImportSuccess('');
      
      const response = await ContactGroupService.importContacts(selectedGroup.id, importFile);
      const results = response.data?.results;
      
      if (results) {
        const message = `Successfully imported contacts!\n\nCreated: ${results.created}\nUpdated: ${results.updated}\nTotal: ${results.total}\nErrors: ${results.errors?.length || 0}`;
        setImportSuccess(message);
        
        // Show errors if any
        if (results.errors && results.errors.length > 0) {
          console.warn('Import errors:', results.errors);
        }
      }
      
      // Refresh the groups list to update contact count
      fetchGroups();
    } catch (error: any) {
      console.error('Error importing contacts:', error);
      setImportError(error.response?.data?.error?.message || 'Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Contact Groups</Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => navigate('/contacts')}>
            Back to Contacts
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingGroup(null);
              setFormData({ name: '', description: '', color: '#1976d2' });
              setOpenDialog(true);
            }}
          >
            New Group
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Color</TableCell>
                <TableCell>Group Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Contacts</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <CircleIcon sx={{ color: group.attributes?.color }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {group.attributes?.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{group.attributes?.description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={`${group.attributes?.contacts?.data?.length || 0} contacts`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={group.attributes?.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={group.attributes?.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      onClick={() => handleImportClick(group)}
                      color="primary"
                      title="Import Contacts"
                    >
                      <UploadIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(group)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(group.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={2}>
            <TextField
              fullWidth
              required
              label="Group Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              fullWidth
              label="Color"
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingGroup ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Contacts Dialog */}
      <Dialog 
        open={openImportDialog} 
        onClose={() => !importing && setOpenImportDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Import Contacts to "{selectedGroup?.attributes?.name}"
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={2}>
            {!importSuccess && (
              <Alert severity="info">
                Upload an Excel (.xlsx, .xls) or CSV file with contact information. 
                The file should include columns: firstName, phone, lastName, email, whatsappNumber, company, etc.
              </Alert>
            )}
            
            {importError && (
              <Alert severity="error">{importError}</Alert>
            )}

            {importSuccess && (
              <Alert severity="success" sx={{ whiteSpace: 'pre-line' }}>
                {importSuccess}
              </Alert>
            )}

            {!importSuccess && (
              <>
                <Box>
                  <Button
                    variant="text"
                    startIcon={<DownloadIcon />}
                    onClick={downloadTemplate}
                    size="small"
                    sx={{ mb: 2 }}
                  >
                    Download Sample Template
                  </Button>
                </Box>

                <Box>
                  <input
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    id="import-file-input"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="import-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      fullWidth
                      disabled={importing}
                    >
                      Choose File
                    </Button>
                  </label>
                  {importFile && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Selected: {importFile.name}
                    </Typography>
                  )}
                </Box>

                <Alert severity="warning">
                  <strong>File Format:</strong>
                  <br />
                  • firstName (required)
                  <br />
                  • phone (required)
                  <br />
                  • lastName, email, whatsappNumber, company, position, address, city, country (optional)
                </Alert>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {!importSuccess && (
            <>
              <Button 
                onClick={() => setOpenImportDialog(false)}
                disabled={importing}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleImport}
                disabled={!importFile || importing}
                startIcon={<UploadIcon />}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </>
          )}
          {importSuccess && (
            <Button 
              variant="contained"
              onClick={() => {
                setOpenImportDialog(false);
                setImportFile(null);
                setSelectedGroup(null);
                setImportSuccess('');
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactGroupList;

